import { Box, Divider, Stack } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { getProjects, linkProject, qk } from 'api'
import { useAuthUser } from 'app/auth'
import { DialogContent, Loader, ToastContent } from 'elements/feedback'
import { Select } from 'elements/inputs'
import { getInputError } from 'lib/form-utils'
import { useInfinitePaginatedQuery } from 'lib/react-query-utils'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

const formId = 'LINK_PROJECT_FORM'

type FormValues = {
  projectIdToLink: string
}

type LinkProjectFormProps = {
  projectId: string
  onClose: () => void
}

export const LinkProjectForm = ({
  projectId,
  onClose,
}: LinkProjectFormProps) => {
  const { t } = useTranslation()

  const { userId } = useAuthUser()

  const queryClient = useQueryClient()

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfinitePaginatedQuery(qk.projects.list, getProjects, {
      pagination: {
        page: 0,
        pageSize: 20,
      },
      ownerId: userId,
    })

  const $linkProject = useMutation(linkProject)

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<FormValues>({
    defaultValues: {
      projectIdToLink: '',
    },
  })

  if (isLoading) return <Loader centered />

  if (!data) return null

  const projects = data.pages

  return (
    <DialogContent
      title={t('projects.settings.link_project.link_project')}
      isActionLoading={$linkProject.isLoading}
      externalFormId={formId}
      onDeny={onClose}
      denyText={t('common.cancel')}
      isConfirmDisabled={!isDirty}
      confirmText={t('common.link')}
    >
      <Box
        id={formId}
        component="form"
        onSubmit={handleSubmit(({ projectIdToLink }) =>
          $linkProject.mutate(
            {
              projectId,
              projectIdToLink,
            },
            {
              onSuccess: () => {
                onClose()
                queryClient.invalidateQueries(
                  qk.projects.details.toKeyWithArgs({ projectId }),
                )
                toast.success(
                  <ToastContent
                    title={t(
                      'projects.settings.link_project.project_successfully_linked',
                    )}
                  />,
                )
              },
            },
          ),
        )}
      >
        <Stack direction="row" spacing={3}>
          <Controller
            name="projectIdToLink"
            control={control}
            render={({ field, fieldState }) => (
              <Select
                label={t(
                  'projects.settings.link_project.select_project_to_link',
                )}
                options={projects.flatMap(page =>
                  page.rows
                    .filter(project => project.projectId !== projectId)
                    .map(project => ({
                      value: project.projectId,
                      label: project.name,
                    })),
                )}
                hasMoreOptions={hasNextPage}
                onLoadMore={fetchNextPage}
                isLoadingMore={isFetchingNextPage}
                {...getInputError(fieldState.error)}
                {...field}
              />
            )}
          />
        </Stack>
      </Box>

      <Divider color="divider" sx={{ mt: 5, borderBottomWidth: 1 }} />
    </DialogContent>
  )
}
